// // src/Pages/ConsignerMaster.jsx
// // ✅ GET    loads grid by companyId on mount
// // ✅ INSERT POST /api/consigner/insert
// // ✅ UPDATE PUT  /api/consigner/update/:conId
// // ✅ DELETE POST /api/consigner/delete — sends { userId, objAryId: [ConID, ...] }
// //           • Toolbar delete button (appears when rows are selected)
// //           • Per-row delete icon (selects that row then opens confirm)

// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import axios from "axios";
// import { Box, Grid, Paper, Typography, TextField, Button, Dialog, DialogContent, CircularProgress, Tooltip, IconButton, Snackbar, useTheme, DialogTitle, DialogActions, CardContent, Card, Checkbox, Avatar, InputAdornment, } from "@mui/material";
// import { Delete as DeleteIcon } from "@mui/icons-material";
// import { DataGrid } from "@mui/x-data-grid";
// import MuiAlert from "@mui/material/Alert";
// import SearchIcon from "@mui/icons-material/Search";
// import PhoneIcon from "@mui/icons-material/Phone";
// import PersonIcon from "@mui/icons-material/Person";
// import ReceiptIcon from "@mui/icons-material/Receipt";
// import { useNavigate, useParams } from "react-router-dom";
// import { useAuth } from "../context/AuthContext";
// import { toProperCase } from "../utils/formatRows";
// import CustomNoDataOverlay from "../components/CustomNoDataOverlay";
// import { useMediaQuery } from "@mui/system";
// import CitySearchInput from "../components/CitySearchInput";
// import CityPopupModal from "../components/CityPopupModal";

// // ─── helpers ──────────────────────────────────────────────────────────────────
// function getInitials(name) {
//     if (!name) return "C";
//     return name.trim().charAt(0).toUpperCase();
// }

// function stringToColor(string) {
//     let hash = 0;
//     for (let i = 0; i < string.length; i++) {
//         hash = string.charCodeAt(i) + ((hash << 5) - hash);
//     }
//     const colors = ["#e74c3c", "#3498db", "#2ecc71", "#e67e22", "#9b59b6", "#1abc9c", "#3f51b5", "#e91e63", "#795548", "#00bcd4"];
//     return colors[Math.abs(hash) % colors.length];
// }

// // ─── constants ────────────────────────────────────────────────────────────────
// const EMPTY_FORM = { ConName: "", Address: "", City: "", Pincode: "", Distance: "", State: "", Statecode: "", GSTNO: "", PANNo: "", ContactPerson: "", Mobile: "", Email: "", };

// const validators = {
//     GSTNO: (v) => v === "" || /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(v),
//     PANNo: (v) => v === "" || /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v),
//     Mobile: (v) => v === "" || /^\d{10}$/.test(v),
//     Email: (v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
//     Pincode: (v) => v === "" || /^\d{1,6}$/.test(v),
// };

// const properCaseFields = ["ConName", "ContactPerson", "Address"];
// const requiredLabelSX = { "& .MuiFormLabel-asterisk": { color: "red" } };
// const inputSX = { "& .MuiOutlinedInput-root": { height: 36, display: "flex", alignItems: "center" }, "& .MuiOutlinedInput-input": { fontSize: "0.85rem", padding: "0 10px" }, };

// // ─── Main Component ───────────────────────────────────────────────────────────
// export default function ConsignerMaster({ onSaved, mode, connectionString, onPopupCancel, onBack, activeViewHandler, }) {
//     const { user, selectedCompany } = useAuth();
//     const role = user?.UserCategory;
//     const theme = useTheme();
//     const API = connectionString;
//     const companyId = selectedCompany?.CompId;
//     const userId = user?.UserId;

//     const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
//     const navigate = useNavigate();
//     const { conId } = useParams();

//     // ── form ──────────────────────────────────────────────────────────────────
//     const [localMode, setLocalMode] = useState(mode || "add");
//     const [form, setForm] = useState(EMPTY_FORM);
//     const [errors, setErrors] = useState({});
//     const [editId, setEditId] = useState(null);

//     // ── city ──────────────────────────────────────────────────────────────────
//     const [cityQuery, setCityQuery] = useState("");
//     const [selectedCity, setSelectedCity] = useState(null);
//     const [cityError, setCityError] = useState("");
//     const [cityResults, setCityResults] = useState([]);
//     const [showCityModal, setShowCityModal] = useState(false);

//     // ── grid ──────────────────────────────────────────────────────────────────
//     const [rows, setRows] = useState([]);
//     const [selectionModel, setSelectionModel] = useState([]);
//     const [loadingGrid, setLoadingGrid] = useState(false);
//     const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
//     const [totalCount, setTotalCount] = useState(0);
//     const [confirmOpen, setConfirmOpen] = useState(false);
//     const [mobileSearchQuery, setMobileSearchQuery] = useState("");
//     const [selectAll, setSelectAll] = useState(false);

//     // ── misc ──────────────────────────────────────────────────────────────────
//     const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

//     // ─────────────────────────────────────────────────────────────────────────
//     // Effects
//     // ─────────────────────────────────────────────────────────────────────────
//     useEffect(() => {
//         if (!companyId) return;
//         fetchGrid();
//     }, [companyId, paginationModel]);

//     useEffect(() => {
//         if (!cityQuery) { setCityResults([]); return; }
//         const t = setTimeout(async () => {
//             try {
//                 const res = await axios.get(`${API}/api/city-search`, { params: { q: cityQuery } });
//                 setCityResults(res.data || []);
//             } catch { setCityResults([]); }
//         }, 400);
//         return () => clearTimeout(t);
//     }, [cityQuery, API]);

//     // ─────────────────────────────────────────────────────────────────────────
//     // API
//     // ─────────────────────────────────────────────────────────────────────────

//     // GET
//     const fetchGrid = useCallback(async () => {
//         if (!companyId) return;
//         setLoadingGrid(true);
//         try {
//             const resp = await axios.get(`${API}/api/consigner-getData`, { headers: { "Cache-Control": "no-store" }, params: { companyId, userId, offset: paginationModel.page * paginationModel.pageSize, limit: paginationModel.pageSize }, });
//             const rawData = resp.data.success && Array.isArray(resp.data?.data) ? resp.data.data : [];
//             const total = resp.data.success && typeof resp.data?.total === "number" ? resp.data.total : 0;
//             const data = rawData.map((row) => ({ ...row, Distance: row.Distance != null ? Math.trunc(Number(row.Distance)) : row.Distance, }));
//             setRows(data);
//             setTotalCount(total);
//         } catch (err) {
//             console.error("GET /api/consigner-getData error:", err);
//             setRows([]);
//             setTotalCount(0);
//             setSnackbar({ open: true, message: "Failed to fetch consigner data.", severity: "error" });
//         } finally {
//             setLoadingGrid(false);
//         }
//     }, [companyId, userId, paginationModel, API]);

//     // INSERT / UPDATE
//     const FN_HandleSubmit = async () => {
//         if (!validateFormBeforeSubmit()) return;
//         const payload = { ...form, companyId, userId, fYear };
//         try {
//             if (localMode === "add") {
//                 await axios.post(`${API}/api/consigner/insert`, payload);
//                 setSnackbar({ open: true, message: "Consigner added successfully!", severity: "success" });
//             } else if (editId != null) {
//                 await axios.put(`${API}/api/consigner/update/${editId}`, payload);
//                 setSnackbar({ open: true, message: "Consigner updated successfully!", severity: "success" });
//             }
//             onSaved?.();
//             resetForm();
//             fetchGrid();
//             if (typeof onPopupCancel === "function") setTimeout(() => onPopupCancel(), 600);
//         } catch (error) {
//             console.error("Save error:", error);
//             setSnackbar({ open: true, message: "Save failed. Please try again.", severity: "error" });
//         }
//     };

//     // ✅ DELETE — sends ConID(s) in objAryId array to POST /api/consigner/delete
//     const handleDelete = async () => {
//         if (!selectionModel.length) return;

//         try {
//             await axios.post(`${API}/api/consigner/delete`, { userId, objAryId: selectionModel, });

//             setSnackbar({ open: true, message: `${selectionModel.length} consigner(s) deleted successfully.`, severity: "success" });
//             setSelectionModel([]);
//             setSelectAll(false);
//             fetchGrid();
//         } catch (error) {
//             const status = error.response?.status;
//             const data = error.response?.data;
//             if (status === 400 && data?.referencedIds?.length > 0)
//                 setSnackbar({ open: true, message: "Cannot delete — item(s) are referenced in other records.", severity: "warning" });
//             else if (status === 400 && data?.error)
//                 setSnackbar({ open: true, message: data.error, severity: "warning" });
//             else
//                 setSnackbar({ open: true, message: "Delete failed due to an unexpected error.", severity: "error" });
//         } finally {
//             setConfirmOpen(false);
//         }
//     };

//     // ─────────────────────────────────────────────────────────────────────────
//     // Form helpers
//     // ─────────────────────────────────────────────────────────────────────────
//     const handleBlur = (key) => () => {
//         const val = form[key] ?? "";
//         if (key === "Pincode") { setErrors((e) => ({ ...e, Pincode: val && val.length !== 6 })); return; }
//         if (!validators[key]) return;
//         setErrors((e) => ({ ...e, [key]: val ? !validators[key](val) : false }));
//     };

//     const handleChange = (key) => (e) => {
//         const { value } = e.target;
//         setForm((f) => ({ ...f, [key]: properCaseFields.includes(key) ? toProperCase(value) : value }));
//     };

//     const resetForm = () => {
//         setLocalMode("add");
//         setEditId(null);
//         setCityQuery("");
//         setSelectedCity(null);
//         setForm(EMPTY_FORM);
//         setErrors({});
//     };

//     const resetAndCancel = () => { resetForm(); onPopupCancel?.(); };
//     const navigateAfterSave = () => { if (activeViewHandler) activeViewHandler(); else navigate(-1); };

//     const validateFormBeforeSubmit = () => {
//         const newErrors = {};
//         if (!form.ConName?.trim()) newErrors.ConName = true;
//         if (form.Pincode && !/^\d{6}$/.test(form.Pincode)) newErrors.Pincode = true;
//         if (form.Distance && !/^\d+$/.test(form.Distance)) newErrors.Distance = true;
//         if (Object.keys(newErrors).length) {
//             setErrors(newErrors);
//             setSnackbar({ open: true, severity: "error", message: "Required fields are missing" });
//             return false;
//         }
//         for (const key of ["GSTNO", "Mobile", "PANNo", "Email"]) {
//             if (form[key] && !validators[key](form[key])) {
//                 setErrors((e) => ({ ...e, [key]: true }));
//                 setSnackbar({ open: true, severity: "error", message: `Invalid ${key}` });
//                 return false;
//             }
//         }
//         setErrors({});
//         return true;
//     };

//     // ✅ Selecting a single row then opening confirm — for per-row delete icon
//     const handleRowDeleteClick = (e, ConID) => {
//         e.stopPropagation();
//         // Select just this row then open confirm
//         setSelectionModel([ConID]);
//         setConfirmOpen(true);
//     };

//     // Edit row (shared by desktop + mobile)
//     const handleEditRow = (row) => {
//         setEditId(row.ConID);
//         setLocalMode("edit");
//         setForm({
//             ConName: row.ConName || "", Address: row.Address || "",
//             City: row.City || "", Pincode: row.Pincode || "",
//             Distance: row.Distance || "", State: row.State || "",
//             Statecode: row.Statecode || "", GSTNO: row.GSTNO || "",
//             PANNo: row.PANNo || "", ContactPerson: row.ContactPerson || "",
//             Mobile: row.Mobile || "", Email: row.Email || "",
//         });
//         setCityQuery(row.City || "");
//         window.scrollTo({ top: 0, behavior: "smooth" });
//     };

//     // ─────────────────────────────────────────────────────────────────────────
//     // Derived state
//     // ─────────────────────────────────────────────────────────────────────────
//     const filteredMobileRows = useMemo(() => {
//         if (!isSmallScreen || !mobileSearchQuery.trim()) return rows;
//         const q = mobileSearchQuery.toLowerCase();
//         return rows.filter((row) =>
//             row.ConName?.toLowerCase().startsWith(q) ||
//             row.GSTNO?.toLowerCase().includes(q) ||
//             row.ContactPerson?.toLowerCase().includes(q)
//         );
//     }, [rows, mobileSearchQuery, isSmallScreen]);

//     const toggleSelectAll = () => {
//         if (selectAll) { setSelectionModel([]); setSelectAll(false); }
//         else { setSelectionModel(filteredMobileRows.map((r) => r.ConID)); setSelectAll(true); }
//     };

//     // ─────────────────────────────────────────────────────────────────────────
//     // DataGrid columns
//     // ─────────────────────────────────────────────────────────────────────────
//     const columns = [
//         {
//             field: "ConName", headerName: "Consigner Details", flex: 1,
//             renderCell: (params) => {
//                 const row = params?.row ?? {};
//                 const ConID = row?.ConID ?? params?.id;
//                 const location = [row.City, row.State].filter(Boolean).join(", ") || "—";
//                 return (
//                     <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
//                         <Typography variant="body2"
//                             sx={{ fontWeight: 600, textDecoration: "underline", color: theme.palette.primary.main, cursor: "pointer" }}
//                             onClick={(e) => { e.stopPropagation(); if (ConID) handleEditRow(row); }}
//                         >
//                             {row.ConName ?? "—"}
//                         </Typography>
//                         <Typography variant="caption" sx={{ color: "text.secondary" }}>{location}</Typography>
//                     </Box>
//                 );
//             },
//         },
//         { field: "Pincode", headerName: "Pincode", flex: 0.4 },
//         { field: "GSTNO", headerName: "GST No.", flex: 0.7 },
//         { field: "Mobile", headerName: "Mobile No.", flex: 0.6 },
//         { field: "ContactPerson", headerName: "Contact", flex: 0.6 },
//         {
//             // ✅ Per-row delete icon — always enabled, selects row + opens confirm
//             field: "__delete", headerName: "", width: 50, sortable: false, filterable: false, disableColumnMenu: true, // action column: no filter / sort / menu
//             renderCell: (params) => {
//                 const ConID = params?.row?.ConID ?? params?.id;
//                 return (
//                     <Tooltip title="Delete this record">
//                         <IconButton
//                             color="error"
//                             size="small"
//                             onClick={(e) => handleRowDeleteClick(e, ConID)}
//                         >
//                             <DeleteIcon fontSize="small" />
//                         </IconButton>
//                     </Tooltip>
//                 );
//             },
//         },
//     ];

//     // ─────────────────────────────────────────────────────────────────────────
//     // Mobile card renderer
//     // ─────────────────────────────────────────────────────────────────────────
//     const renderConsignerCards = () => {
//         if (loadingGrid)
//             return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>;
//         if (filteredMobileRows.length === 0)
//             return <Box sx={{ p: 4, textAlign: "center" }}><CustomNoDataOverlay /></Box>;

//         return (
//             <>
//                 {filteredMobileRows.map((row) => {
//                     const isSelected = selectionModel.includes(row.ConID);
//                     const avatarColor = stringToColor(row.ConName || "C");
//                     const locationText = [row.City, row.State].filter(Boolean).join(", ") || "—";

//                     return (
//                         <Card key={row.ConID}
//                             sx={{ mb: 1.5, boxShadow: 3, borderRadius: 2, overflow: "hidden", transition: "0.2s", "&:hover": { boxShadow: 5 }, borderLeft: "4px solid", borderLeftColor: "primary.main", cursor: "pointer" }}
//                             onClick={() => handleEditRow(row)}
//                         >
//                             <CardContent sx={{ py: 1.5, px: 2 }}>
//                                 <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
//                                     {role !== "User" && (
//                                         <Checkbox size="small" checked={isSelected}
//                                             onClick={(e) => e.stopPropagation()}
//                                             onChange={() => setSelectionModel((prev) =>
//                                                 isSelected ? prev.filter((id) => id !== row.ConID) : [...prev, row.ConID]
//                                             )}
//                                             sx={{ mr: 0.5 }}
//                                         />
//                                     )}
//                                     <Avatar sx={{ bgcolor: avatarColor, width: 34, height: 34, fontWeight: "bold", mr: 1.5 }}>
//                                         {getInitials(row.ConName)}
//                                     </Avatar>
//                                     <Box sx={{ flexGrow: 1, minWidth: 0 }}>
//                                         <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: "underline", color: theme.palette.primary.main }}>
//                                             {row.ConName || "—"}
//                                         </Typography>
//                                         <Typography variant="caption" color="text.secondary">{locationText}</Typography>
//                                     </Box>

//                                     {/* ✅ Per-card delete button (mobile) */}
//                                     <Tooltip title="Delete">
//                                         <IconButton size="small" color="error"
//                                             onClick={(e) => handleRowDeleteClick(e, row.ConID)}
//                                         >
//                                             <DeleteIcon fontSize="small" />
//                                         </IconButton>
//                                     </Tooltip>
//                                 </Box>

//                                 <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
//                                     <Box sx={{ display: "flex", alignItems: "center" }}>
//                                         <PersonIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
//                                         <Typography variant="body2" color="text.secondary">{row.ContactPerson || "—"}</Typography>
//                                     </Box>
//                                     <Box sx={{ display: "flex", alignItems: "center" }}>
//                                         <PhoneIcon fontSize="small" sx={{ mr: 1, color: "primary.main" }} />
//                                         <Typography variant="body2" sx={{ fontWeight: 500, color: "primary.main" }}>{row.Mobile || "—"}</Typography>
//                                     </Box>
//                                     <Box sx={{ display: "flex", alignItems: "center" }}>
//                                         <ReceiptIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
//                                         <Typography variant="body2" color="text.secondary">GST: {row.GSTNO || "—"}</Typography>
//                                     </Box>
//                                 </Box>
//                             </CardContent>
//                         </Card>
//                     );
//                 })}

//                 {/* Mobile pagination */}
//                 <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, py: 2 }}>
//                     <Button variant="outlined" size="small" disabled={paginationModel.page === 0}
//                         onClick={() => setPaginationModel((p) => ({ ...p, page: Math.max(0, p.page - 1) }))}>
//                         ← Prev
//                     </Button>
//                     <Typography variant="body2">
//                         Page {paginationModel.page + 1} / {Math.ceil(totalCount / paginationModel.pageSize) || 1}
//                     </Typography>
//                     <Button variant="outlined" size="small"
//                         disabled={(paginationModel.page + 1) * paginationModel.pageSize >= totalCount}
//                         onClick={() => setPaginationModel((p) => ({ ...p, page: p.page + 1 }))}>
//                         Next →
//                     </Button>
//                 </Box>
//             </>
//         );
//     };

//     // ─────────────────────────────────────────────────────────────────────────
//     // Render
//     // ─────────────────────────────────────────────────────────────────────────
//     return (
//         <Box sx={{ px: 2, py: 1 }}>
//             {/* ── Header ──────────────────────────────────────────────────── */}
//             <Box
//                 display="flex"
//                 justifyContent="space-between"
//                 alignItems="center"
//                 mb={1.5}
//             >
//                 <Typography variant="h6" fontWeight={700} fontSize="1rem">
//                     {localMode === "add" ? "Add Consigner" : "Edit Consigner"}
//                 </Typography>
//                 <Box display="flex" gap={1} alignItems="center">
//                     {/* ✅ Toolbar delete — visible when ≥1 rows selected */}
//                     {selectionModel.length > 0 && role !== "User" && (
//                         <Button
//                             size="small"
//                             variant="outlined"
//                             color="error"
//                             startIcon={<DeleteIcon />}
//                             onClick={() => setConfirmOpen(true)}
//                         >
//                             Delete ({selectionModel.length})
//                         </Button>
//                     )}
//                     <Button size="small" variant="contained" onClick={FN_HandleSubmit}>
//                         {localMode === "add" ? "Save" : "Update"}
//                     </Button>
//                     <Button
//                         size="small"
//                         variant="outlined"
//                         color="error"
//                         onClick={() => {
//                             resetForm();
//                             if (typeof onPopupCancel === "function") {
//                                 onPopupCancel();
//                             }
//                         }}
//                     >
//                         Cancel
//                     </Button>
//                 </Box>
//             </Box>

//             {/* ── Compact Form ────────────────────────────────────────────── */}
//             <Paper sx={{ p: 1.5, mb: 1.5, border: "1px solid #e0e0e0" }}>
//                 <Grid container spacing={1}>
//                     {/* Row 1 */}
//                     <Grid item size={{ xs: 12, sm: 3 }}>
//                         <TextField
//                             fullWidth
//                             size="small"
//                             label="Consigner Name *"
//                             value={form.ConName}
//                             error={Boolean(errors.ConName)}
//                             helperText={errors.ConName ? "Required" : ""}
//                             InputLabelProps={{ sx: requiredLabelSX }}
//                             sx={inputSX}
//                             onChange={(e) =>
//                                 handleChange("ConName")({
//                                     target: { value: toProperCase(e.target.value) },
//                                 })
//                             }
//                         />
//                     </Grid>
//                     <Grid item xs={12} md={2}>
//                         <TextField
//                             fullWidth
//                             size="small"
//                             label="Mobile"
//                             value={form.Mobile}
//                             error={Boolean(errors.Mobile)}
//                             helperText={errors.Mobile ? "10 digits" : ""}
//                             onBlur={handleBlur("Mobile")}
//                             sx={inputSX}
//                             onChange={(e) => {
//                                 const v = e.target.value.replace(/\D/g, "").slice(0, 10);
//                                 handleChange("Mobile")({ target: { value: v } });
//                             }}
//                         />
//                     </Grid>
//                     <Grid item xs={12} md={2}>
//                         <TextField
//                             fullWidth
//                             size="small"
//                             label="GST No."
//                             value={form.GSTNO}
//                             error={Boolean(errors.GSTNO)}
//                             helperText={errors.GSTNO ? "Invalid GST" : ""}
//                             onBlur={handleBlur("GSTNO")}
//                             sx={inputSX}
//                             onChange={(e) => {
//                                 const v = e.target.value
//                                     .toUpperCase()
//                                     .replace(/[^0-9A-Z]/g, "")
//                                     .slice(0, 15);
//                                 handleChange("GSTNO")({ target: { value: v } });
//                             }}
//                         />
//                     </Grid>
//                     <Grid item xs={12} md={2}>
//                         <TextField
//                             fullWidth
//                             size="small"
//                             label="PAN No."
//                             value={form.PANNo}
//                             error={Boolean(errors.PANNo)}
//                             helperText={errors.PANNo ? "Invalid PAN" : ""}
//                             onBlur={handleBlur("PANNo")}
//                             sx={inputSX}
//                             inputProps={{ maxLength: 10 }}
//                             onChange={(e) => {
//                                 const v = e.target.value
//                                     .toUpperCase()
//                                     .replace(/[^A-Z0-9]/g, "")
//                                     .slice(0, 10);
//                                 handleChange("PANNo")({ target: { value: v } });
//                             }}
//                         />
//                     </Grid>
//                     <Grid item size={{ xs: 12, sm: 3 }}>
//                         <TextField
//                             fullWidth
//                             size="small"
//                             label="Contact Person"
//                             value={form.ContactPerson}
//                             sx={inputSX}
//                             onChange={(e) =>
//                                 handleChange("ContactPerson")({
//                                     target: { value: toProperCase(e.target.value) },
//                                 })
//                             }
//                         />
//                     </Grid>

//                     {/* Row 2 */}
//                     <Grid item xs={12} md={4}>
//                         <TextField
//                             fullWidth
//                             size="small"
//                             label="Address"
//                             value={form.Address}
//                             sx={inputSX}
//                             onChange={(e) =>
//                                 handleChange("Address")({
//                                     target: { value: toProperCase(e.target.value) },
//                                 })
//                             }
//                         />
//                     </Grid>
//                     <Grid item xs={6} md={2}>
//                         <TextField
//                             fullWidth
//                             size="small"
//                             label="Pincode"
//                             value={form.Pincode}
//                             error={Boolean(errors.Pincode)}
//                             helperText={errors.Pincode ? "6 digits" : ""}
//                             onBlur={handleBlur("Pincode")}
//                             sx={inputSX}
//                             inputProps={{ maxLength: 6 }}
//                             onChange={(e) =>
//                                 setForm((f) => ({
//                                     ...f,
//                                     Pincode: e.target.value.replace(/\D/g, ""),
//                                 }))
//                             }
//                         />
//                     </Grid>
//                     <Grid item xs={6} md={2}>
//                         <TextField
//                             fullWidth
//                             size="small"
//                             label="Distance (km)"
//                             value={form.Distance}
//                             sx={inputSX}
//                             inputProps={{ maxLength: 6 }}
//                             onChange={(e) => {
//                                 const v = e.target.value;
//                                 if (/^\d*$/.test(v)) setForm((f) => ({ ...f, Distance: v }));
//                             }}
//                         />
//                     </Grid>
//                     <Grid item xs={12} md={4}>
//                         <CitySearchInput
//                             value={selectedCity?.city || cityQuery}
//                             cities={cityResults}
//                             connectionString={connectionString}
//                             onChangeQuery={(q) => {
//                                 setCityQuery(q);
//                                 if (cityError) setCityError("");
//                             }}
//                             onSelectCity={(c) => {
//                                 setSelectedCity(c);
//                                 setForm((f) => ({
//                                     ...f,
//                                     City: c.city,
//                                     State: c.state,
//                                     Statecode: c.stateCode,
//                                 }));
//                                 setCityQuery("");
//                                 setCityError("");
//                             }}
//                         />
//                     </Grid>

//                     {/* Row 3 */}
//                     <Grid item xs={6} md={3}>
//                         <TextField
//                             size="small"
//                             fullWidth
//                             label="State"
//                             value={form.State}
//                             disabled
//                             sx={inputSX}
//                         />
//                     </Grid>
//                     <Grid item xs={3} md={1}>
//                         <TextField
//                             size="small"
//                             fullWidth
//                             label="Code"
//                             value={form.Statecode}
//                             disabled
//                             sx={inputSX}
//                         />
//                     </Grid>
//                     <Grid item size={{ xs: 12, sm: 3 }}>
//                         <TextField
//                             fullWidth
//                             size="small"
//                             label="Email"
//                             value={form.Email}
//                             error={Boolean(errors.Email)}
//                             helperText={errors.Email ? "Invalid email" : ""}
//                             onBlur={handleBlur("Email")}
//                             sx={inputSX}
//                             onChange={handleChange("Email")}
//                         />
//                     </Grid>
//                 </Grid>
//             </Paper>

//             {/* ── Grid ────────────────────────────────────────────────────── */}
//             {isSmallScreen ? (
//                 /* Mobile view */
//                 <>
//                     <Box sx={{ mb: 1 }}>
//                         <TextField
//                             fullWidth
//                             size="small"
//                             placeholder="Search consigners..."
//                             value={mobileSearchQuery}
//                             onChange={(e) => setMobileSearchQuery(e.target.value)}
//                             InputProps={{
//                                 startAdornment: (
//                                     <InputAdornment position="start">
//                                         <SearchIcon fontSize="small" />
//                                     </InputAdornment>
//                                 ),
//                             }}
//                         />
//                     </Box>

//                     {/* Bulk delete toolbar for mobile */}
//                     {role !== "User" && selectionModel.length > 0 && (
//                         <Box
//                             sx={{
//                                 display: "flex",
//                                 alignItems: "center",
//                                 justifyContent: "space-between",
//                                 px: 2,
//                                 py: 1,
//                                 mb: 1.5,
//                                 bgcolor: "background.paper",
//                                 borderRadius: 2,
//                                 boxShadow: 1,
//                             }}
//                         >
//                             <Box sx={{ display: "flex", alignItems: "center" }}>
//                                 <Checkbox
//                                     checked={selectAll}
//                                     onChange={toggleSelectAll}
//                                     size="small"
//                                 />
//                                 <Typography variant="body2" sx={{ ml: 1 }}>
//                                     {selectionModel.length} selected
//                                 </Typography>
//                             </Box>
//                             <Tooltip title="Delete selected">
//                                 <IconButton
//                                     onClick={() => setConfirmOpen(true)}
//                                     color="error"
//                                     size="small"
//                                 >
//                                     <DeleteIcon />
//                                 </IconButton>
//                             </Tooltip>
//                         </Box>
//                     )}

//                     <Box sx={{ width: "100%", overflowX: "hidden" }}>
//                         {renderConsignerCards()}
//                     </Box>
//                 </>
//             ) : (
//                 /* Desktop DataGrid */
//                 <Box sx={{ height: 380 }}>
//                     <DataGrid
//                         rows={rows}
//                         columns={columns}
//                         getRowId={(r) => r.ConID}
//                         checkboxSelection
//                         rowSelectionModel={selectionModel}
//                         onRowSelectionModelChange={(s) => setSelectionModel(s)}
//                         columnHeaderHeight={38}
//                         rowHeight={40}
//                         disableRowSelectionOnClick
//                         loading={loadingGrid}
//                         pagination
//                         paginationMode="server"
//
//
//                         rowCount={totalCount}
//                         onPageChange={(page) =>
//                             setPaginationModel((p) => ({ ...p, page }))
//                         }
//                         onPageSizeChange={(sz) =>
//                             setPaginationModel((p) => ({ ...p, pageSize: sz }))
//                         }
//                         pageSizeOptions={[25, 50, 100]}
//                         slots={{ noResultsOverlay: CustomNoDataOverlay }}
//                         sx={{
//                             fontSize: "0.82rem",
//                             "& .MuiDataGrid-columnHeader": { backgroundColor: themeColors.primary.surface, color: themeColors.neutral.sub, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
//                             "& .MuiDataGrid-filler": { backgroundColor: "#fff" },
//                             "& .MuiDataGrid-scrollbarFiller": {
//                                 backgroundColor: "#f0f0f0",
//                             },
//                             "& .MuiDataGrid-row:nth-of-type(odd)": {
//                                 backgroundColor: "#fff",
//                             },
//                             "& .MuiDataGrid-row:nth-of-type(even)": {
//                                 backgroundColor: "#fafafa",
//                             },
//                             "& .MuiDataGrid-row:hover": { backgroundColor: themeColors.primary.tint },
//                             "& .MuiDataGrid-row.Mui-selected": {
//                                 backgroundColor: "#e3eeff !important",
//                             },
//                             "& .MuiDataGrid-cell": {
//                                 display: "flex",
//                                 alignItems: "center",
//                             },
//                             "& .MuiDataGrid-footerContainer": { minHeight: 36, height: 36 },
//                         }}
//                     />
//                 </Box>
//             )}

//             {/* ── City popup ──────────────────────────────────────────────── */}
//             {showCityModal && (
//                 <CityPopupModal
//                     onClose={() => setShowCityModal(false)}
//                     onCityAdded={(c) => {
//                         setForm((f) => ({
//                             ...f,
//                             City: c.city,
//                             State: c.state,
//                             Statecode: c.stateCode,
//                         }));
//                         setCityQuery(c.city);
//                         setShowCityModal(false);
//                     }}
//                 />
//             )}

//             {/* ── Snackbar ─────────────────────────────────────────────────── */}
//             <Snackbar
//                 open={snackbar.open}
//                 autoHideDuration={5000}
//                 onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
//                 anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
//             >
//                 <MuiAlert
//                     elevation={6}
//                     variant="filled"
//                     severity={snackbar.severity}
//                     sx={{ width: "100%" }}
//                     onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
//                 >
//                     {snackbar.message}
//                 </MuiAlert>
//             </Snackbar>

//             {/* ── Delete confirm dialog ───────────────────────────────────── */}
//             <Dialog
//                 open={confirmOpen}
//                 onClose={() => setConfirmOpen(false)}
//                 maxWidth="xs"
//                 fullWidth
//             >
//                 <DialogTitle>Confirm Deletion</DialogTitle>
//                 <DialogContent>
//                     <Typography variant="body2">
//                         Are you sure you want to delete{" "}
//                         <strong>{selectionModel.length}</strong> consigner
//                         {selectionModel.length !== 1 ? "s" : ""}? This action{" "}
//                         <strong>cannot be undone</strong>.
//                     </Typography>
//                 </DialogContent>
//                 <DialogActions>
//                     <Button variant="outlined" onClick={() => setConfirmOpen(false)}>
//                         Cancel
//                     </Button>
//                     <Button
//                         color="error"
//                         variant="contained"
//                         onClick={() => {
//                             setConfirmOpen(false);
//                             handleDelete();
//                         }}
//                     >
//                         Delete
//                     </Button>
//                 </DialogActions>
//             </Dialog>
//         </Box>
//     );
// }






// src/Pages/ConsignerMaster.jsx
// ✅ GET    loads grid by companyId on mount
// ✅ INSERT POST /api/consigner/insert
// ✅ UPDATE PUT  /api/consigner/update/:conId
// ✅ DELETE POST /api/consigner/delete — sends { userId, objAryId: [ConID, ...] }
//           • Toolbar delete button (appears when rows are selected)
//           • Per-row delete icon (selects that row then opens confirm)

import { colors as themeColors } from '../utils/theme';
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Box, Grid, Paper, Typography, TextField, Button, Dialog, DialogContent, CircularProgress, Tooltip, IconButton, Snackbar, useTheme, DialogTitle, DialogActions, CardContent, Card, Checkbox, Avatar, Chip, Divider, InputAdornment, LinearProgress, } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import MuiAlert from "@mui/material/Alert";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toProperCase } from "../utils/formatRows";
import { getCurrentFYear } from '../utils/financialYear';
import CustomNoDataOverlay from "../components/CustomNoDataOverlay";
import { useMediaQuery } from "@mui/system";
import CitySearchInput from "../components/CitySearchInput";
// import CityPopupModal from "../../components/CityPopupModal";
import CityPopupModal from "../components/CityPopupModal";
import { colors, gradients } from '../utils/theme';

// ─── helpers ──────────────────────────────────────────────────────────────────
function getInitials(name) {
    if (!name) return "C";
    return name.trim().charAt(0).toUpperCase();
}

function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ["#e74c3c", "#3498db", "#2ecc71", "#e67e22", "#9b59b6", "#1abc9c", "#3f51b5", "#e91e63", "#795548", "#00bcd4"];
    return colors[Math.abs(hash) % colors.length];
}

// ─── constants ────────────────────────────────────────────────────────────────
const EMPTY_FORM = { ConName: "", Address: "", City: "", Pincode: "", Distance: "", State: "", Statecode: "", GSTNO: "", PANNo: "", ContactPerson: "", Mobile: "", Email: "", };

const validators = {
    GSTNO: (v) => v === "" || /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(v),
    PANNo: (v) => v === "" || /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v),
    Mobile: (v) => v === "" || /^\d{10}$/.test(v),
    Email: (v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
    Pincode: (v) => v === "" || /^\d{1,6}$/.test(v),
};

const properCaseFields = ["ConName", "ContactPerson", "Address"];
const requiredLabelSX = { "& .MuiFormLabel-asterisk": { color: "red" } };
const inputSX = { "& .MuiOutlinedInput-root": { height: 36, display: "flex", alignItems: "center" }, "& .MuiOutlinedInput-input": { fontSize: "0.85rem", padding: "0 10px" }, };

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ConsignerMaster({ onSaved, mode, connectionString, onPopupCancel, onBack, activeViewHandler, }) {
    const { user, selectedCompany } = useAuth();
    const role = user?.UserCategory;
    const theme = useTheme();
    const API = connectionString;
    const companyId = selectedCompany?.CompId;
    const userId = user?.UserId;
    const fYear = getCurrentFYear();

    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    const navigate = useNavigate();
    const { conId } = useParams();

    // ── form ──────────────────────────────────────────────────────────────────
    const [localMode, setLocalMode] = useState("add"); // ✅ Add mode by default
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [editId, setEditId] = useState(null);

    // ── city ──────────────────────────────────────────────────────────────────
    const [cityQuery, setCityQuery] = useState("");
    const [selectedCity, setSelectedCity] = useState(null);
    const [cityError, setCityError] = useState("");
    const [cityResults, setCityResults] = useState([]);
    const [showCityModal, setShowCityModal] = useState(false);



    // ── grid ──────────────────────────────────────────────────────────────────
    const [rows, setRows] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);
    const [loadingGrid, setLoadingGrid] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [totalCount, setTotalCount] = useState(0);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [mobileSearchQuery, setMobileSearchQuery] = useState("");
    const [selectAll, setSelectAll] = useState(false);

    // ── misc ──────────────────────────────────────────────────────────────────
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

 const requiredAsteriskStyle = { '& .MuiFormLabel-asterisk': { color: 'red', }, };

    // ─────────────────────────────────────────────────────────────────────────
    // Effects
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!companyId) return;
        fetchGrid();
    }, [companyId, paginationModel]);

    useEffect(() => {
        if (!cityQuery) { setCityResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const res = await axios.get(`${API}/api/city-search`, { params: { q: cityQuery } });
                setCityResults(res.data || []);
            } catch { setCityResults([]); }
        }, 400);
        return () => clearTimeout(t);
    }, [cityQuery, API]);

    // ─────────────────────────────────────────────────────────────────────────
    // API
    // ─────────────────────────────────────────────────────────────────────────

    // GET
    const fetchGrid = useCallback(async () => {
        if (!companyId) return;
        setLoadingGrid(true);
        try {
            const resp = await axios.get(`${API}/api/consigner-getData`, { headers: { "Cache-Control": "no-store" }, params: { companyId, userId, offset: paginationModel.page * paginationModel.pageSize, limit: paginationModel.pageSize }, });
            const rawData = resp.data.success && Array.isArray(resp.data?.data) ? resp.data.data : [];
            const total = resp.data.success && typeof resp.data?.total === "number" ? resp.data.total : 0;
            const data = rawData.map((row) => ({ ...row, Distance: row.Distance != null ? Math.trunc(Number(row.Distance)) : row.Distance, }));
            setRows(data);
            setTotalCount(total);
        } catch (err) {
            console.error("GET /api/consigner-getData error:", err);
            setRows([]);
            setTotalCount(0);
            setSnackbar({ open: true, message: "Failed to fetch consigner data.", severity: "error" });
        } finally {
            setLoadingGrid(false);
        }
    }, [companyId, userId, paginationModel, API]);

    // INSERT / UPDATE
    const FN_HandleSubmit = async () => {
        if (!validateFormBeforeSubmit()) return;
        const payload = { ...form, companyId, userId, fYear };
        try {
            if (localMode === "add") {
                await axios.post(`${API}/api/consigner/insert`, payload);
                setSnackbar({ open: true, message: "Consigner added successfully!", severity: "success" });
            } else if (editId != null) {
                await axios.put(`${API}/api/consigner/update/${editId}`, payload);
                setSnackbar({ open: true, message: "Consigner updated successfully!", severity: "success" });
            }
            onSaved?.();
            resetForm();
            fetchGrid();
            if (typeof onPopupCancel === "function") setTimeout(() => onPopupCancel(), 600);
        } catch (error) {
            console.error("Save error:", error);
            const status = error.response?.status;
            const data = error.response?.data;
            
            // ✅ Handle duplication error
            if (status === 409) {
                setSnackbar({ open: true, message: data?.message || "Consigner already exists with this name and city.", severity: "error" });
            } else {
                setSnackbar({ open: true, message: "Save failed. Please try again.", severity: "error" });
            }
        }
    };

    // ✅ DELETE — sends ConID(s) in objAryId array to POST /api/consigner/delete
    const handleDelete = async () => {
        if (!selectionModel.length || deleting) return;

        setDeleting(true);
        try {
            await axios.post(`${API}/api/consigner/delete`, { userId, objAryId: selectionModel, });

            setSnackbar({ open: true, message: `${selectionModel.length} consigner(s) deleted successfully.`, severity: "success" });
            setSelectionModel([]);
            setSelectAll(false);
            fetchGrid();
        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data;
            if (status === 400 && data?.referencedIds?.length > 0)
                setSnackbar({ open: true, message: "Cannot delete — item(s) are referenced in other records.", severity: "warning" });
            else if (status === 400 && data?.error)
                setSnackbar({ open: true, message: data.error, severity: "warning" });
            else
                setSnackbar({ open: true, message: "Delete failed due to an unexpected error.", severity: "error" });
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Form helpers
    // ─────────────────────────────────────────────────────────────────────────
    const handleBlur = (key) => () => {
        const val = form[key] ?? "";
        if (key === "Pincode") { setErrors((e) => ({ ...e, Pincode: val && val.length !== 6 })); return; }
        if (!validators[key]) return;
        setErrors((e) => ({ ...e, [key]: val ? !validators[key](val) : false }));
    };

    const handleChange = (key) => (e) => {
        const { value } = e.target;
        setForm((f) => ({ ...f, [key]: properCaseFields.includes(key) ? toProperCase(value) : value }));
    };

    const resetForm = () => {
        setLocalMode("add"); // ✅ Reset to Add mode
        setEditId(null);
        setCityQuery("");
        setSelectedCity(null);
        setForm(EMPTY_FORM);
        setErrors({});
    };

    const resetAndCancel = () => { resetForm(); onPopupCancel?.(); };
    const navigateAfterSave = () => { if (activeViewHandler) activeViewHandler(); else navigate(-1); };

    // ──────────────── Alt+S (Save/Update) & Alt+C (Cancel) shortcuts ────────────────
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey) return;

            if (e.code === "KeyS") {
                e.preventDefault();
                FN_HandleSubmit();
            } else if (e.code === "KeyC") {
                e.preventDefault();
                resetAndCancel();
            }
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    });

    const validateFormBeforeSubmit = () => {
        const newErrors = {};
        
        // ✅ ConName is required
        if (!form.ConName?.trim()) newErrors.ConName = true;
        
        // ✅ City is required
        if (!form.City?.trim()) newErrors.City = true;
        
        if (form.Pincode && !/^\d{6}$/.test(form.Pincode)) newErrors.Pincode = true;
        if (form.Distance && !/^\d+$/.test(form.Distance)) newErrors.Distance = true;
        
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            setSnackbar({ open: true, severity: "error", message: "Consigner Name and City are required" });
            return false;
        }
        
        for (const key of ["GSTNO", "Mobile", "PANNo", "Email"]) {
            if (form[key] && !validators[key](form[key])) {
                setErrors((e) => ({ ...e, [key]: true }));
                setSnackbar({ open: true, severity: "error", message: `Invalid ${key}` });
                return false;
            }
        }
        
        setErrors({});
        return true;
    };

    // ✅ Selecting a single row then opening confirm — for per-row delete icon
    const handleRowDeleteClick = (e, ConID) => {
        e.stopPropagation();
        // Select just this row then open confirm
        setSelectionModel([ConID]);
        setConfirmOpen(true);
    };

    // ✅ Edit row (shared by desktop + mobile) — triggered by clicking consigner name
    const handleEditRow = (row) => {
        setEditId(row.ConID);
        setLocalMode("edit");
        setForm({
            ConName: row.ConName || "", Address: row.Address || "",
            City: row.City || "", Pincode: row.Pincode || "",
            Distance: row.Distance || "", State: row.State || "",
            Statecode: row.Statecode || "", GSTNO: row.GSTNO || "",
            PANNo: row.PANNo || "", ContactPerson: row.ContactPerson || "",
            Mobile: row.Mobile || "", Email: row.Email || "",
        });
        setCityQuery(row.City || "");
        setSelectedCity({ city: row.City, state: row.State, stateCode: row.Statecode });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Derived state
    // ─────────────────────────────────────────────────────────────────────────
    const filteredMobileRows = useMemo(() => {
        if (!isSmallScreen || !mobileSearchQuery.trim()) return rows;
        const q = mobileSearchQuery.toLowerCase();
        return rows.filter((row) =>
            row.ConName?.toLowerCase().startsWith(q) ||
            row.GSTNO?.toLowerCase().includes(q) ||
            row.ContactPerson?.toLowerCase().includes(q)
        );
    }, [rows, mobileSearchQuery, isSmallScreen]);

    const toggleSelectAll = () => {
        if (selectAll) { setSelectionModel([]); setSelectAll(false); }
        else { setSelectionModel(filteredMobileRows.map((r) => r.ConID)); setSelectAll(true); }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // DataGrid columns
    // ─────────────────────────────────────────────────────────────────────────
    const columns = [
        {
            field: "ConName", headerName: "Consigner Details", flex: 1,
            renderCell: (params) => {
                const row = params?.row ?? {};
                const ConID = row?.ConID ?? params?.id;
                const location = [row.City, row.State].filter(Boolean).join(", ") || "—";
                return (
                    <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                        {/* ✅ Click on ConsignerName to edit */}
                        <Typography variant="body2"
                            sx={{ fontWeight: 600, textDecoration: "underline", color: theme.palette.primary.main, cursor: "pointer" }}
                            onClick={(e) => { e.stopPropagation(); if (ConID) handleEditRow(row); }}
                        >
                            {row.ConName ?? "—"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>{location}</Typography>
                    </Box>
                );
            },
        },
        { field: "Pincode", headerName: "Pincode", flex: 0.4 },
        { field: "GSTNO", headerName: "GST No.", flex: 0.7 },
        { field: "Mobile", headerName: "Mobile No.", flex: 0.6 },
        { field: "ContactPerson", headerName: "Contact", flex: 0.6 },
        {
            // ✅ Per-row delete icon — always enabled, selects row + opens confirm
            field: "__delete", headerName: "", width: 50, sortable: false, filterable: false, disableColumnMenu: true, // action column: no filter / sort / 3-dot menu
            renderCell: (params) => {
                const ConID = params?.row?.ConID ?? params?.id;
                return (
                    <Tooltip title="Delete this record">
                        <IconButton
                            color="error"
                            size="small"
                            onClick={(e) => handleRowDeleteClick(e, ConID)}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
    ];

    // ─────────────────────────────────────────────────────────────────────────
    // Mobile card renderer
    // ─────────────────────────────────────────────────────────────────────────
    const renderConsignerCards = () => {
        if (loadingGrid)
            return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>;
        if (filteredMobileRows.length === 0)
            return <Box sx={{ p: 4, textAlign: "center" }}><CustomNoDataOverlay /></Box>;

        return (
            <>
                {filteredMobileRows.map((row) => {
                    const isSelected = selectionModel.includes(row.ConID);
                    const avatarColor = stringToColor(row.ConName || "C");
                    const locationText = [row.City, row.State].filter(Boolean).join(", ") || "—";

                    return (
                        <Card key={row.ConID}
                            sx={{ mb: 1.5, borderRadius: 2, boxShadow: 1, overflow: "hidden", transition: "0.2s", "&:hover": { boxShadow: 3 }, cursor: "pointer" }}
                            onClick={() => handleEditRow(row)}
                        >
                            <Box sx={{ height: 5, background: `linear-gradient(90deg, ${avatarColor} 0%, ${avatarColor}88 100%)` }} />
                            <CardContent sx={{ py: 1.5, px: 1.5 }}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                                    {role !== "User" && (
                                        <Checkbox size="small" checked={isSelected}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => setSelectionModel((prev) =>
                                                isSelected ? prev.filter((id) => id !== row.ConID) : [...prev, row.ConID]
                                            )}
                                            sx={{ p: 0.5, mr: 0.5, flexShrink: 0 }}
                                        />
                                    )}
                                    <Avatar sx={{ bgcolor: avatarColor, color: themeColors.neutral.surface, width: 38, height: 38, fontWeight: 700, mr: 1.5, flexShrink: 0, fontSize: "1rem" }}>
                                        {getInitials(row.ConName)}
                                    </Avatar>
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: colors.primary.main, cursor: "pointer", textDecoration: "underline", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {row.ConName || "—"}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{locationText}</Typography>
                                    </Box>
                                    <Tooltip title="Delete">
                                        <IconButton size="small" color="error" onClick={(e) => handleRowDeleteClick(e, row.ConID)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Divider sx={{ my: 0.8 }} />
                                <Box sx={{ mt: 0.8 }}>
                                    {row.ContactPerson && (
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                            <PersonIcon sx={{ fontSize: "0.9rem", color: "text.secondary", mr: 0.8 }} />
                                            <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{row.ContactPerson}</Typography>
                                        </Box>
                                    )}
                                    <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap", mt: 0.5 }}>
                                        {row.Mobile && <Chip color="primary" icon={<PhoneIcon />} label={row.Mobile} size="small" sx={{ height: 22, fontSize: "0.75rem" }} />}
                                        {row.GSTNO && <Chip variant="outlined" icon={<ReceiptIcon />} label={`GST: ${row.GSTNO}`} size="small" sx={{ height: 22, fontSize: "0.75rem" }} />}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}

                {/* Mobile pagination */}
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, py: 2 }}>
                    <Button variant="outlined" size="small" startIcon={<NavigateBeforeIcon />}
                        disabled={paginationModel.page === 0}
                        onClick={() => setPaginationModel((p) => ({ ...p, page: Math.max(0, p.page - 1) }))}>
                        Previous
                    </Button>
                    <Typography variant="body2">
                        Page {paginationModel.page + 1} / {Math.ceil(totalCount / paginationModel.pageSize) || 1}
                    </Typography>
                    <Button variant="outlined" size="small" endIcon={<NavigateNextIcon />}
                        disabled={(paginationModel.page + 1) * paginationModel.pageSize >= totalCount}
                        onClick={() => setPaginationModel((p) => ({ ...p, page: p.page + 1 }))}>
                        Next
                    </Button>
                </Box>
            </>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ px: 0, py: 0 }}>
            {/* ── Header ──────────────────────────────────────────────────── */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1.5}
            >
                <Typography variant="h6" fontWeight={700} fontSize="1rem">
                    {localMode === "add" ? "Add Consigner" : "Edit Consigner"}
                </Typography>
                <Box display="flex" gap={1} alignItems="center">
                    {/* ✅ Toolbar delete — visible when ≥1 rows selected */}
                    {selectionModel.length > 0 && role !== "User" && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => setConfirmOpen(true)}
                        >
                            Delete ({selectionModel.length})
                        </Button>
                    )}
                    <Button size="small" variant="contained" onClick={FN_HandleSubmit}>
                        {localMode === "add" ? "Save" : "Update"}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => {
                            resetForm();
                            if (typeof onPopupCancel === "function") {
                                onPopupCancel();
                            }
                        }}
                    >
                        Cancel
                    </Button>
                </Box>
            </Box>

            {/* ── Compact Form ────────────────────────────────────────────── */}
            <Paper sx={{ p: 1.5, mb: 1.5, border: `1px solid ${themeColors.neutral.line}` }}>
                <Grid container spacing={1}>
                    {/* Row 1 */}
                    <Grid item size={{xs:12, sm:3}}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Consigner Name"
                            value={form.ConName}
                            error={Boolean(errors.ConName)}
                            helperText={errors.ConName ? "Required" : ""}
                            sx={inputSX}
                            onChange={(e) =>
                                handleChange("ConName")({
                                    target: { value: toProperCase(e.target.value) },
                                })
                            }
                            required
                            InputLabelProps={{ sx: requiredAsteriskStyle }}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Mobile"
                            value={form.Mobile}
                            error={Boolean(errors.Mobile)}
                            helperText={errors.Mobile ? "10 digits" : ""}
                            onBlur={handleBlur("Mobile")}
                            sx={inputSX}
                            onChange={(e) => {
                                const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                                handleChange("Mobile")({ target: { value: v } });
                            }}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="GST No."
                            value={form.GSTNO}
                            error={Boolean(errors.GSTNO)}
                            helperText={errors.GSTNO ? "Invalid GST" : ""}
                            onBlur={handleBlur("GSTNO")}
                            sx={inputSX}
                            onChange={(e) => {
                                const v = e.target.value
                                    .toUpperCase()
                                    .replace(/[^0-9A-Z]/g, "")
                                    .slice(0, 15);
                                handleChange("GSTNO")({ target: { value: v } });
                            }}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="PAN No."
                            value={form.PANNo}
                            error={Boolean(errors.PANNo)}
                            helperText={errors.PANNo ? "Invalid PAN" : ""}
                            onBlur={handleBlur("PANNo")}
                            sx={inputSX}
                            inputProps={{ maxLength: 10 }}
                            onChange={(e) => {
                                const v = e.target.value
                                    .toUpperCase()
                                    .replace(/[^A-Z0-9]/g, "")
                                    .slice(0, 10);
                                handleChange("PANNo")({ target: { value: v } });
                            }}
                        />
                    </Grid>

                    {/* Row 2 */}
                    <Grid item size={{ xs: 12, sm: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Contact Person"
                            value={form.ContactPerson}
                            sx={inputSX}
                            onChange={(e) =>
                                handleChange("ContactPerson")({
                                    target: { value: toProperCase(e.target.value) },
                                })
                            }
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Address"
                            value={form.Address}
                            sx={inputSX}
                            onChange={(e) =>
                                handleChange("Address")({
                                    target: { value: toProperCase(e.target.value) },
                                })
                            }
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Pincode"
                            value={form.Pincode}
                            error={Boolean(errors.Pincode)}
                            helperText={errors.Pincode ? "6 digits" : ""}
                            onBlur={handleBlur("Pincode")}
                            sx={inputSX}
                            inputProps={{ maxLength: 6 }}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    Pincode: e.target.value.replace(/\D/g, ""),
                                }))
                            }
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Distance (km)"
                            value={form.Distance}
                            sx={inputSX}
                            inputProps={{ maxLength: 6 }}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (/^\d*$/.test(v)) setForm((f) => ({ ...f, Distance: v }));
                            }}
                        />
                    </Grid>

                    {/* Row 3 */}
                    <Grid item size={{ xs: 12, sm: 3 }}>
                        <CitySearchInput
                            value={selectedCity?.city || cityQuery}
                            cities={cityResults}
                            connectionString={connectionString}
                            onChangeQuery={(q) => {
                                setCityQuery(q);
                                if (cityError) setCityError("");
                            }}
                            onSelectCity={(c) => {
                                setSelectedCity(c);
                                setForm((f) => ({
                                    ...f,
                                    City: c.city,
                                    State: c.state,
                                    Statecode: c.stateCode,
                                }));
                                setCityQuery("");
                                setCityError("");
                            }}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 3 }}>
                        <TextField
                            size="small"
                            fullWidth
                            label="State"
                            value={form.State}
                            disabled
                            sx={inputSX}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 3 }}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Code"
                            value={form.Statecode}
                            disabled
                            sx={inputSX}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Email"
                            value={form.Email}
                            error={Boolean(errors.Email)}
                            helperText={errors.Email ? "Invalid email" : ""}
                            onBlur={handleBlur("Email")}
                            sx={inputSX}
                            onChange={handleChange("Email")}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* ── Grid ────────────────────────────────────────────────────── */}
            {isSmallScreen ? (
                /* Mobile view */
                <>
                    <Box sx={{ mb: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search consigners..."
                            value={mobileSearchQuery}
                            onChange={(e) => setMobileSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Bulk delete toolbar for mobile */}
                    {role !== "User" && selectionModel.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 2,
                                py: 1,
                                mb: 1.5,
                                bgcolor: "background.paper",
                                borderRadius: 2,
                                boxShadow: 1,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Checkbox
                                    checked={selectAll}
                                    onChange={toggleSelectAll}
                                    size="small"
                                />
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    {selectionModel.length} selected
                                </Typography>
                            </Box>
                            <Tooltip title="Delete selected">
                                <IconButton
                                    onClick={() => setConfirmOpen(true)}
                                    color="error"
                                    size="small"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    <Box sx={{ width: "100%", overflowX: "hidden" }}>
                        {renderConsignerCards()}
                    </Box>
                </>
            ) : (
                /* Desktop DataGrid */
                <Box sx={{ height: 380 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(r) => r.ConID}
                        checkboxSelection
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={(s) => setSelectionModel(s)}
                        columnHeaderHeight={38}
                        rowHeight={40}
                        disableRowSelectionOnClick
                        loading={loadingGrid}
                        pagination
                        paginationMode="server"


                        rowCount={totalCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        slots={{ noResultsOverlay: CustomNoDataOverlay }}
                        sx={{
                            fontSize: "0.82rem",
                            "& .MuiDataGrid-columnHeader": { backgroundColor: themeColors.primary.surface, color: themeColors.neutral.sub, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
                            "& .MuiDataGrid-filler": { backgroundColor: themeColors.neutral.surface },
                            "& .MuiDataGrid-scrollbarFiller": {
                                backgroundColor: themeColors.neutral.border,
                            },
                            "& .MuiDataGrid-row:nth-of-type(odd)": {
                                backgroundColor: themeColors.neutral.surface,
                            },
                            "& .MuiDataGrid-row:nth-of-type(even)": {
                                backgroundColor: themeColors.neutral.surfaceAlt,
                            },
                            "& .MuiDataGrid-row:hover": { backgroundColor: themeColors.primary.tint },
                            "& .MuiDataGrid-row.Mui-selected": {
                                backgroundColor: `${themeColors.primary.selected} !important`,
                            },
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                            },
                            "& .MuiDataGrid-footerContainer": { minHeight: 36, height: 36 },
                        }}
                    />
                </Box>
            )}

            {/* ── City popup ──────────────────────────────────────────────── */}
            {showCityModal && (
                <CityPopupModal
                    onClose={() => setShowCityModal(false)}
                    onCityAdded={(c) => {
                        setForm((f) => ({
                            ...f,
                            City: c.city,
                            State: c.state,
                            Statecode: c.stateCode,
                        }));
                        setCityQuery(c.city);
                        setShowCityModal(false);
                    }}
                />
            )}

            {/* ── Snackbar ─────────────────────────────────────────────────── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <MuiAlert
                    elevation={6}
                    variant="filled"
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>

            {/* ── Delete confirm dialog ───────────────────────────────────── */}
            <Dialog
                open={confirmOpen}
                onClose={() => { if (!deleting) setConfirmOpen(false); }}
                maxWidth="xs"
                fullWidth
            >
                {deleting
                    ? <LinearProgress color="error" sx={{ height: 4 }} />
                    : <Box sx={{ height: 4, background: gradients.danger }} />}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>
                        Confirm Deletion
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete{' '}
                        <Typography component="span" variant="body2" fontWeight={700} color="error.main">
                            {selectionModel.length}
                        </Typography>
                        {' '}consigner record(s)? This action cannot be undone.
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} variant="outlined" color="inherit" size="small" disabled={deleting}>Cancel</Button>
                    <Button color="error" variant="contained" size="small" disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                        onClick={() => handleDelete()}>{deleting ? 'Deleting…' : 'Delete'}</Button>
                </Box>
            </Dialog>
        </Box>
    );
}